<template>
  <div class="pricing">
    <h1>{{ $t('Plans') }}</h1>
    <p class="lead">{{ $t('Every plan has all the features of a program: saving, reports, exports. The plans differ by the programs they open.') }}</p>

    <div class="interval">
      <button :class="{ on: interval === 'month' }" @click="interval = 'month'">{{ $t('Monthly') }}</button>
      <button :class="{ on: interval === 'year' }" @click="interval = 'year'">
        {{ $t('Yearly') }} <span v-if="saving" class="save">{{ $t('2 months free') }}</span>
      </button>
    </div>

    <p v-if="needed" class="notice">{{ needed }}</p>
    <p v-if="error" class="error-message">{{ error }}</p>

    <div v-if="data" class="cards">
      <div v-for="plan in data.plans" :key="plan.key" class="card" :class="{ featured: plan.key === 'pro' }">
        <h2>{{ $t(plan.name === 'Free' ? 'Free' : plan.name) }}</h2>

        <p class="price" v-if="plan.contact">{{ $t("Let's talk") }}</p>
        <p class="price" v-else-if="plan.monthly === 0">$0</p>
        <p class="price" v-else>
          ${{ interval === 'year' ? plan.yearly : plan.monthly }}
          <small>/ {{ $t(interval === 'year' ? 'year' : 'month') }}</small>
        </p>
        <p class="sub">
          <template v-if="(plan.key === 'pro' || plan.key === 'plus' || plan.key === 'pro_plus') && interval === 'year'">${{ perMonth(plan) }} {{ $t('a month, billed yearly') }}</template>
          <template v-else-if="plan.key === 'free'">{{ $t('Free forever, no card') }}</template>
          <template v-else-if="plan.education">{{ $t('Free with a verified .edu.tr e-mail address') }}</template>
          <template v-else-if="plan.contact">{{ $t('Per-user pricing, coming soon') }}</template>
          <template v-else>{{ $t('Cancel any time') }}</template>
        </p>

        <ul>
          <li v-for="feature in plan.features" :key="feature">{{ $t(feature) }}</li>
        </ul>

        <template v-if="plan.key === 'free'">
          <button class="btn" disabled>{{ tier === 'free' ? $t('Your plan') : $t('Included') }}</button>
        </template>
        <template v-else-if="plan.education">
          <button class="btn" disabled>{{ eduActive ? $t('Active on your account') : $t('Sign up with your .edu.tr address') }}</button>
        </template>
        <template v-else-if="plan.key === 'plus'">
          <button v-if="tier === 'plus'" class="btn btn-primary" :disabled="busy" @click="manage">
            {{ busy ? $t('Opening...') : $t('Manage subscription') }}
          </button>
          <button v-else-if="['pro', 'pro_plus', 'enterprise'].includes(tier)" class="btn" disabled>{{ $t('Included in your plan') }}</button>
          <button v-else-if="plan.purchasable" class="btn btn-primary" :disabled="busy" @click="upgrade('plus')">
            {{ busy ? $t('Opening...') : $t('Upgrade to Plus') }}
          </button>
          <button v-else class="btn" disabled>{{ $t('Coming soon') }}</button>
        </template>
        <template v-else-if="plan.key === 'pro'">
          <button v-if="tier === 'pro'" class="btn btn-primary" :disabled="busy" @click="manage">
            {{ busy ? $t('Opening...') : $t('Manage subscription') }}
          </button>
          <button v-else-if="tier === 'pro_plus' || tier === 'enterprise'" class="btn" disabled>{{ $t('Included in your plan') }}</button>
          <button v-else-if="tier === 'plus'" class="btn btn-primary" :disabled="busy" @click="manage">
            {{ busy ? $t('Opening...') : $t('Change plan') }}
          </button>
          <button v-else-if="plan.purchasable" class="btn btn-primary" :disabled="busy" @click="upgrade('pro')">
            {{ busy ? $t('Opening...') : $t('Upgrade to Pro') }}
          </button>
          <button v-else class="btn" disabled>{{ $t('Coming soon') }}</button>
        </template>
        <template v-else-if="plan.key === 'pro_plus'">
          <button v-if="tier === 'pro_plus'" class="btn btn-primary" :disabled="busy" @click="manage">
            {{ busy ? $t('Opening...') : $t('Manage subscription') }}
          </button>
          <button v-else-if="tier === 'enterprise'" class="btn" disabled>{{ $t('Included in your plan') }}</button>
          <button v-else-if="tier === 'plus' || tier === 'pro'" class="btn btn-primary" :disabled="busy" @click="manage">
            {{ busy ? $t('Opening...') : $t('Change plan') }}
          </button>
          <button v-else-if="plan.purchasable" class="btn btn-primary" :disabled="busy" @click="upgrade('pro_plus')">
            {{ busy ? $t('Opening...') : $t('Upgrade to Pro Plus') }}
          </button>
          <button v-else class="btn" disabled>{{ $t('Coming soon') }}</button>
        </template>
        <a v-else class="btn" :href="teamMail">{{ $t('Contact us') }}</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import api from '../api'
import { useAuthStore } from '../stores/auth'
import { useRoute } from 'vue-router'
import { t } from '../i18n'
import { PROGRAMS } from '../programs'

const authStore = useAuthStore()
const route = useRoute()

// Sent here from a program the plan does not open: say which plan does.
const needed = computed(() => {
  const key = String(route.query.program || '')
  const required = data.value?.programs?.[key]
  if (!PROGRAMS[key] || !required || required === 'free') return ''
  return t('{program} is part of the {plan} plan.', {
    program: t(PROGRAMS[key].name), plan: { plus: 'Plus', pro_plus: 'Pro Plus' }[required] || 'Pro',
  })
})
const data = ref(null)
const interval = ref('year')
const busy = ref(false)
const error = ref('')

const tier = computed(() => authStore.user?.tier || 'free')
const eduActive = computed(() => {
  const email = (authStore.user?.email || '').toLowerCase()
  return Boolean(authStore.isVerified && /@([a-z0-9-]+\.)*edu\.tr$/.test(email) && tier.value !== 'free')
})
const pro = computed(() => data.value?.plans.find((p) => p.key === 'pro'))
const saving = computed(() => pro.value && pro.value.yearly < pro.value.monthly * 12)
const teamMail = computed(
  () => `mailto:${data.value?.contact || ''}?subject=${encodeURIComponent('SlipSurface IQ Team plan')}`
)

const perMonth = (plan) => (plan.yearly / 12).toFixed(2).replace(/\.00$/, '')

const go = async (request) => {
  busy.value = true
  error.value = ''
  try {
    const response = await request()
    window.location.href = response.data.url
  } catch (err) {
    busy.value = false
    error.value = t(err.response?.data?.detail || 'Could not reach the payment service')
  }
}

const upgrade = (plan) => go(() => api.post('/api/billing/checkout', { interval: interval.value, plan }))
const manage = () => go(() => api.post('/api/billing/portal'))

onMounted(async () => {
  try {
    data.value = (await api.get('/api/billing/plans')).data
    await authStore.fetchUser()
  } catch (err) {
    error.value = t('Could not load the plans')
  }
})
</script>

<style scoped>
.notice {
  margin: 0 0 16px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fff3cd;
  color: #7a5b00;
  font-size: 14px;
}

.pricing {
  padding: 20px;
  max-width: 1180px;
  margin: 0 auto;
  text-align: center;
}

h1 {
  margin: 0 0 6px 0;
  font-size: 32px;
}

.lead {
  margin: 0 0 20px 0;
  color: #666;
}

.dark .lead {
  color: #aaa;
}

.interval {
  display: inline-flex;
  margin-bottom: 24px;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
}

.interval button {
  padding: 8px 18px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.interval button.on {
  background: #4CAF50;
  color: white;
}

.save {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  background: #fff3cd;
  color: #856404;
  font-size: 12px;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 20px;
  text-align: left;
}

.card {
  color: #222;
  display: flex;
  flex-direction: column;
  padding: 24px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark .card {
  color: #e8e8e8;
  background: #2a2a2a;
}

.card.featured {
  border-color: #4CAF50;
}

.card h2 {
  margin: 0 0 8px 0;
  font-size: 20px;
}

.price {
  margin: 0;
  font-size: 36px;
  font-weight: 700;
}

.price small {
  font-size: 15px;
  font-weight: 400;
  color: #777;
}

.sub {
  margin: 4px 0 16px 0;
  color: #777;
  font-size: 13px;
}

ul {
  flex: 1;
  margin: 0 0 20px 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.7;
}

.btn {
  display: block;
  padding: 10px 16px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}

.btn-primary {
  border-color: #4CAF50;
  background: #4CAF50;
  color: white;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.error-message {
  padding: 12px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
}
</style>
